/**
 * useAutoSave Hook
 *
 * Automatically saves data with debounce after changes.
 * Used for simple fields that don't require explicit save action.
 */

'use client';

import { useEffect, useRef, useState, useCallback } from 'react';

export type AutoSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

interface UseAutoSaveOptions<T> {
  /** Data to auto-save */
  data: T;
  /** Save function that returns a promise */
  onSave: (data: T) => Promise<void>;
  /** Debounce delay in milliseconds (default: 2000) */
  debounceMs?: number;
  /** Whether auto-save is enabled (default: true) */
  enabled?: boolean;
  /** Callback on successful save */
  onSuccess?: () => void;
  /** Callback on save error */
  onError?: (error: Error) => void;
  /** Custom comparison to detect changes (defaults to JSON.stringify) */
  compare?: (prev: T, next: T) => boolean;
}

interface UseAutoSaveReturn {
  /** Current save status */
  status: AutoSaveStatus;
  /** Last save timestamp */
  lastSaved: Date | null;
  /** Error if save failed */
  error: Error | null;
  /** Manually trigger save (bypasses debounce) */
  saveNow: () => Promise<void>;
  /** Cancel pending save */
  cancel: () => void;
  /** Whether there are pending changes */
  isPending: boolean;
}

export function useAutoSave<T>({
  data,
  onSave,
  debounceMs = 2000,
  enabled = true,
  onSuccess,
  onError,
  compare,
}: UseAutoSaveOptions<T>): UseAutoSaveReturn {
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedDataRef = useRef<T>(data);
  const isMountedRef = useRef(true);

  // Default comparison
  const defaultCompare = useCallback((prev: T, next: T): boolean => {
    return JSON.stringify(prev) === JSON.stringify(next);
  }, []);

  const compareValues = compare ?? defaultCompare;

  const hasChanges = !compareValues(lastSavedDataRef.current, data);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const performSave = useCallback(async () => {
    if (!isMountedRef.current) return;

    setStatus('saving');
    setError(null);

    try {
      await onSave(data);

      if (isMountedRef.current) {
        lastSavedDataRef.current = data;
        setStatus('saved');
        setLastSaved(new Date());
        onSuccess?.();

        // Reset to idle after showing "saved" status
        setTimeout(() => {
          if (isMountedRef.current) {
            setStatus('idle');
          }
        }, 2000);
      }
    } catch (err) {
      if (isMountedRef.current) {
        const saveError = err instanceof Error ? err : new Error('Save failed');
        setError(saveError);
        setStatus('error');
        onError?.(saveError);
      }
    }
  }, [data, onSave, onSuccess, onError]);

  // Debounced auto-save
  useEffect(() => {
    if (!enabled || !hasChanges) {
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setStatus('pending');

    timeoutRef.current = setTimeout(() => {
      performSave();
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, enabled, hasChanges, debounceMs, performSave]);

  const saveNow = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    await performSave();
  }, [performSave]);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setStatus('idle');
  }, []);

  return {
    status,
    lastSaved,
    error,
    saveNow,
    cancel,
    isPending: status === 'pending',
  };
}
